import { Map, MapCameraChangedEvent, InfoWindow, AdvancedMarker } from "@vis.gl/react-google-maps";
import { Box, CircularProgress, Typography } from "@mui/material";
import AdjustIcon from "@mui/icons-material/Adjust";
import { Map as MapIcon } from "@mui/icons-material";
import { Polygon as MapPolygon, Polyline as MapPolyline } from "../../JobsDashboard/map";
import { MarkerWithInfoWindow } from "../../JobsDashboard/map/MarkerWithInfoWindow";
import type { JobMapViewModel } from "./JobMap.types";
import "./JobMap.css";

interface JobMapMapProps {
	vm: JobMapViewModel;
}

/** The Google Maps rendering layer — receives a pre-computed view-model. */
export function JobMapMap({ vm }: JobMapMapProps) {
	const isBoundaryMode = vm.colorMode === "boundary-hatch" || vm.colorMode === "boundary-outline";

	// Empty state
	if (vm.showEmptyState && !vm.hasGeodata) {
		return (
			<Box
				sx={{
					height: vm.resolvedHeight ?? 200,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: "grey.50",
					borderRadius: vm.resolvedHeight === "100%" ? 0 : 1,
					mt: vm.resolvedHeight === "100%" ? 0 : 2,
					border: 1,
					borderColor: "divider",
					gap: 1,
				}}
			>
				<MapIcon sx={{ fontSize: 48, color: "text.disabled" }} />
				<Typography variant="body2" color="text.secondary">
					No data to display
				</Typography>
			</Box>
		);
	}

	// Loading state
	if (!vm.mapsLibraryLoaded) {
		if (vm.showEmptyState) {
			return (
				<Box
					sx={{
						height: vm.resolvedHeight ?? 200,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						bgcolor: "grey.100",
						borderRadius: vm.resolvedHeight === "100%" ? 0 : 1,
						mt: vm.resolvedHeight === "100%" ? 0 : 2,
					}}
				>
					<CircularProgress size={24} />
				</Box>
			);
		}
		return <div>Loading...</div>;
	}

	const mapContent = (
		<Map
			className={`portal-map${vm.effectiveLassoMode ? " lasso-mode" : ""}`}
			style={vm.resolvedHeight ? { height: vm.resolvedHeight } : undefined}
			mapId={"customerMaps"}
			zoom={vm.zoom}
			onBoundsChanged={(e: MapCameraChangedEvent) => {
				vm.onBoundsChanged(e.detail.bounds);
			}}
			onZoomChanged={(e: MapCameraChangedEvent) => {
				vm.onZoomChanged(e.detail.zoom);
			}}
			onClick={vm.onMapClick}
			defaultCenter={vm.center}
			defaultZoom={10}
			disableDefaultUI={vm.disableDefaultUI}
			zoomControl={vm.disableDefaultUI ? vm.zoomControl : undefined}
			gestureHandling={vm.gestureHandling}
		>
			{/* Polygons */}
			{vm.visibleMapPolygons.map((poly) => (
				<MapPolygon
					key={poly.id}
					fillOpacity={poly.fillOpacity}
					fillColor={poly.fillColor}
					strokeWeight={poly.strokeWeight ?? 2}
					strokeColor={poly.strokeColor}
					strokeOpacity={poly.strokeOpacity}
					paths={poly.paths}
					clickable={poly.clickable !== undefined ? poly.clickable : !isBoundaryMode && !vm.effectiveLassoMode}
					onClick={(e: google.maps.MapMouseEvent) => {
						if (e.latLng && poly.job) {
							vm.onPolygonClick(poly.job, e.latLng.toJSON());
						}
					}}
				/>
			))}

			{/* Polylines (hatch lines, boundary outlines) */}
			{vm.visiblePolylines.map((line) => (
				<MapPolyline
					key={line.id}
					path={line.path}
					strokeColor={line.strokeColor}
					strokeWeight={line.strokeWeight}
					strokeOpacity={line.strokeOpacity}
				/>
			))}

			{/* Markers */}
			{isBoundaryMode
				? // Boundary modes: render dot-style markers
					vm.visibleMapMarkers.map((marker) =>
						marker.dotStyle ? (
							<AdvancedMarker key={marker.id} position={marker.position} zIndex={marker.dotStyle.zIndex}>
								<div
									style={{
										width: marker.dotStyle.size,
										height: marker.dotStyle.size,
										backgroundColor: marker.dotStyle.color,
										borderRadius: "50%",
										border: marker.dotStyle.border,
										boxShadow: marker.dotStyle.shadow,
									}}
								/>
							</AdvancedMarker>
						) : null,
					)
				: vm.colorMode === "layer-type"
					? vm.visibleMapMarkers.map((marker) => (
							<AdvancedMarker key={marker.id} position={marker.position}>
								<AdjustIcon sx={{ color: marker.simpleColor || "#f44336", fontSize: 14 }} />
							</AdvancedMarker>
						))
					: vm.visibleMapMarkers.map((marker) => (
							<MarkerWithInfoWindow
								key={marker.id}
								iconColor={marker.iconColor}
								position={marker.position}
								clickable={!vm.effectiveLassoMode}
								infoWindowText={
									<Typography variant="body2">
										Sample ID: {marker.sampleId}
										<br />
										Barcode: <b>{marker.barcode}</b>
										<br />
										Moved? {marker.hasChange ? `Yes (${marker.changeType})` : "No"}
										{marker.changeReason ? `: ${marker.changeReason}` : ""}
									</Typography>
								}
								label={marker.sampleId || ""}
							/>
						))}

			{/* Info window (suppressed in boundary modes) */}
			{!isBoundaryMode && vm.activePopup && (
				<InfoWindow position={vm.activePopup.position} onCloseClick={vm.onInfoWindowClose}>
					<Typography variant="body2">
						<strong>{vm.activePopup.job.importFieldName || vm.activePopup.job.fieldName || "Unnamed Field"}</strong>
						{(vm.activePopup.job.importFarmName || vm.activePopup.job.farmName) && (
							<>
								<br />
								Farm: {vm.activePopup.job.importFarmName || vm.activePopup.job.farmName}
							</>
						)}
						{(vm.activePopup.job.importGrowerName || vm.activePopup.job.growerName) && (
							<>
								<br />
								Grower: {vm.activePopup.job.importGrowerName || vm.activePopup.job.growerName}
							</>
						)}
					</Typography>
				</InfoWindow>
			)}
		</Map>
	);

	// Wrap in Box for preview-style usage (border radius, overflow hidden)
	if (vm.showEmptyState) {
		return (
			<Box
				sx={{
					height: vm.resolvedHeight ?? 200,
					borderRadius: vm.resolvedHeight === "100%" ? 0 : 1,
					overflow: "hidden",
					mt: vm.resolvedHeight === "100%" ? 0 : 2,
				}}
			>
				{mapContent}
			</Box>
		);
	}

	return mapContent;
}
